<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <open-to-work-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">Requests</h4>
          <p class="text-muted mb-4">
            Companies who've asked for your resume or invited you to join their
            workforce.
          </p>

          <div v-if="dashboardStats" class="otw-dashboard-stats mb-4">
            <div class="otw-dashboard-stats__tile">
              <span class="otw-dashboard-stats__value">{{
                dashboardStats.resume_requests_received
              }}</span>
              <span class="otw-dashboard-stats__label"
                >Resume Requests Received</span
              >
            </div>
            <div class="otw-dashboard-stats__tile">
              <span class="otw-dashboard-stats__value">{{
                dashboardStats.resume_requests_accepted
              }}</span>
              <span class="otw-dashboard-stats__label"
                >Resume Requests Accepted</span
              >
            </div>
            <div class="otw-dashboard-stats__tile">
              <span class="otw-dashboard-stats__value">{{
                dashboardStats.workforce_invitations_received
              }}</span>
              <span class="otw-dashboard-stats__label"
                >Workforce Invitations Received</span
              >
            </div>
            <div class="otw-dashboard-stats__tile">
              <span class="otw-dashboard-stats__value">{{
                dashboardStats.workforce_invitations_accepted
              }}</span>
              <span class="otw-dashboard-stats__label"
                >Workforce Invitations Accepted</span
              >
            </div>
          </div>

          <h5 class="font-weight-bold mb-3">Resume Requests</h5>
          <b-alert
            :show="!hasResume && !!pendingResumeRequestCount"
            variant="warning"
            class="mb-3"
          >
            You need a resume on file before you can accept a resume request.
            <router-link :to="{ name: 'user.settings-page', hash: '#profile' }">
              Upload a resume
            </router-link>
          </b-alert>
          <div
            v-if="incoming.resume_requests.length"
            class="otw-request-list mb-4"
          >
            <div
              v-for="item in incoming.resume_requests"
              :key="item.id"
              class="otw-request-item"
            >
              <div>
                <span class="otw-request-item__company">{{
                  item.company ? item.company.name : 'A company'
                }}</span>
                <span class="text-muted small d-block">{{
                  formatDate(item.created_at)
                }}</span>
              </div>
              <div class="otw-request-item__actions">
                <b-badge
                  v-if="item.status !== 'pending'"
                  :class="`otw-status-badge--${item.status}`"
                  class="otw-status-badge"
                >
                  {{ statusLabel(item.status) }}
                </b-badge>
                <template v-else>
                  <span v-b-tooltip.hover="acceptTooltip">
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="busyId === item.id || !hasResume"
                      @click="respondResume(item, 'accepted')"
                    >
                      Accept
                    </b-button>
                  </span>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    :disabled="busyId === item.id"
                    @click="respondResume(item, 'declined')"
                  >
                    Decline
                  </b-button>
                </template>
              </div>
            </div>
          </div>
          <p v-else-if="!isLoading" class="text-muted mb-4">
            No resume requests yet.
          </p>

          <h5 class="font-weight-bold mb-3">Workforce Invitations</h5>
          <div
            v-if="incoming.workforce_invitations.length"
            class="otw-request-list"
          >
            <div
              v-for="item in incoming.workforce_invitations"
              :key="item.id"
              class="otw-request-item"
            >
              <div>
                <span class="otw-request-item__company">{{
                  item.company ? item.company.name : 'A company'
                }}</span>
                <span class="text-muted small d-block">{{
                  formatDate(item.created_at)
                }}</span>
              </div>
              <div class="otw-request-item__actions">
                <b-badge
                  v-if="item.status !== 'pending'"
                  :class="`otw-status-badge--${item.status}`"
                  class="otw-status-badge"
                >
                  {{ statusLabel(item.status) }}
                </b-badge>
                <template v-else>
                  <b-button
                    variant="primary"
                    size="sm"
                    :disabled="busyId === item.id"
                    @click="respondWorkforce(item, 'accepted')"
                  >
                    Accept
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    :disabled="busyId === item.id"
                    @click="respondWorkforce(item, 'declined')"
                  >
                    Decline
                  </b-button>
                </template>
              </div>
            </div>
          </div>
          <p v-else-if="!isLoading" class="text-muted">
            No workforce invitations yet.
          </p>
        </b-col>
      </b-row>
    </b-container>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      :variant="alertType"
      dismissible
      fade
      @dismiss-count-down="(val) => (dismissCountDown = val)"
    >
      {{ alertMsg }}
    </b-alert>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import OpenToWorkTabs from '@/components/openToWork/OpenToWorkTabs.vue'

const STATUS_LABEL = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  token_pending: 'Awaiting Token',
  active: 'Active',
}

export default {
  name: 'OpenToWorkRequestsPage',

  components: {
    OpenToWorkTabs,
  },

  data() {
    return {
      isLoading: false,
      busyId: null,
      dismissCountDown: 0,
      alertMsg: '',
      alertType: 'success',
    }
  },

  computed: {
    ...mapGetters('tradieOtw', ['incoming', 'dashboardStats']),
    ...mapGetters('auth', ['user']),

    // Mirrors the server-side gate in TradieOtwRequestService::
    // respondToResumeRequest() — accepting means "yes, here's my resume",
    // which needs an actual file to back it up.
    hasResume() {
      return !!(this.user && this.user.resume)
    },

    pendingResumeRequestCount() {
      return this.incoming.resume_requests.filter(
        (item) => item.status === 'pending',
      ).length
    },

    acceptTooltip() {
      if (this.hasResume) return { title: '', disabled: true }
      return {
        title: 'Upload a resume before you can accept a resume request.',
        disabled: false,
      }
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchIncoming(), this.fetchDashboardStats()])
    } catch (err) {
      this.onError(err)
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    ...mapActions('tradieOtw', [
      'fetchIncoming',
      'fetchDashboardStats',
      'respondToResumeRequest',
      'respondToWorkforceInvitation',
    ]),

    statusLabel(status) {
      return STATUS_LABEL[status] || status
    },

    formatDate(value) {
      if (!value) return ''
      // Force AU day-first format (dd/mm/yyyy); a bare toLocaleDateString()
      // follows the browser/OS locale and renders US mm/dd/yyyy here.
      return new Date(value).toLocaleDateString('en-AU')
    },

    async respondResume(item, status) {
      this.busyId = item.id
      try {
        await this.respondToResumeRequest({ id: item.id, status })
        this.showToast(
          'Success',
          status === 'accepted'
            ? 'Resume request accepted.'
            : 'Resume request declined.',
        )
        this.fetchDashboardStats().catch(() => {})
      } catch (err) {
        this.onError(err)
      } finally {
        this.busyId = null
      }
    },

    async respondWorkforce(item, status) {
      this.busyId = item.id
      try {
        await this.respondToWorkforceInvitation({ id: item.id, status })
        this.showToast(
          'Success',
          status === 'accepted'
            ? 'Workforce invitation accepted - you have joined this company.'
            : 'Workforce invitation declined.',
        )
        this.fetchDashboardStats().catch(() => {})
      } catch (err) {
        this.onError(err)
      } finally {
        this.busyId = null
      }
    },

    onError(err) {
      const message =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong.'
      this.alertMsg = message
      this.alertType = 'danger'
      this.dismissCountDown = 5
    },

    showToast(title, message) {
      this.alertMsg = message
      this.alertType = 'success'
      this.dismissCountDown = 3
    },
  },
}
</script>

<style lang="scss" scoped>
// Explicit column counts per breakpoint (1 / 2 / 4), not auto-fit. With
// exactly 4 tiles, auto-fit(minmax(160px, 1fr)) picks however many columns
// happen to fit the current width — at some widths that's 3, leaving the
// 4th tile stranded alone on its own row. Pinning the count to a divisor of
// 4 at each breakpoint keeps it always a full grid: 1x4, 2x2, or 4x1.
.otw-dashboard-stats {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 0.75rem;

  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.otw-dashboard-stats__tile {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid #e1e6ea;
  background-color: #fff;
}

.otw-dashboard-stats__value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #212529;
}

.otw-dashboard-stats__label {
  font-size: 0.75rem;
  color: #6c7581;
}

.otw-request-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.otw-request-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid #e1e6ea;
  background-color: #fff;
}

.otw-request-item__company {
  font-weight: 700;
}

.otw-request-item__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.otw-status-badge {
  font-weight: 600;
  font-size: 0.72rem;
  padding: 0.3rem 0.65rem;
}

.otw-status-badge--accepted,
.otw-status-badge--active {
  background-color: #eaf6ee;
  color: #1f7a3a;
}

.otw-status-badge--declined {
  background-color: #fdecee;
  color: #a3242e;
}

.otw-status-badge--expired {
  background-color: #eceef1;
  color: #495057;
}

.otw-status-badge--token_pending {
  background-color: #fdf6e3;
  color: #8a6a16;
}
</style>
